import { ActionTree } from 'vuex'
import { RootState } from './types'
import Cookie from 'js-cookie'
import i18n from 'i18n'

import {
	ProductInfoRequest,
	PlaceOrderRequest,
	CustomerSurvey,
	PurchasePosition,
	OrderSummaryRequest,
	ValidateEmailRequest,
	ValidateDiscountRequest,
	PlaceOrderOk,
	Discount as PbDiscount,
	BasketError,
	SurveyField,
} from 'proto/eshop/Basket_pb'
import { BasketClient } from 'proto/eshop/BasketService_pb_service'
import { grpc } from '@improbable-eng/grpc-web'

import Loading from '@/components/services/Loading'
import { promisify } from '@/utils/promisify'
import { unpackResponse } from '@/service/ResponseValidators'
import { handleError } from '@/service/ErrorHandlers'
import { observableStream } from '@/service/ObservableStream'
import { DiscountError } from '@/service/DiscountError'
import config from '@/config'

const { WebsocketTransport } = grpc

const AUTH_COOKIE_NAME = 'auth_sid'

const client = new BasketClient(config.apiUrl, {
	transport: WebsocketTransport(),
})

export const actions: ActionTree<RootState, RootState> = {

	async streamState({ dispatch }, id) {
		const request = new OrderSummaryRequest()
		request.setOrderId(id)

		const stream = observableStream(await client.orderSummary(request))
		stream.listen((response) => {
			dispatch('setState', response.getPaymentstatus())
		})

		return stream
	},

	async fetchProductInfo(ctx, { id }) {
		const authSid = Cookie.get(AUTH_COOKIE_NAME)

		const request = new ProductInfoRequest()
		request.setSaleRegion(config.saleRegion)
		request.setProductId(id)
		if (authSid) request.setAuthSid(authSid)

		const productInfo = promisify(client.productInfo, client)
		try {
			Loading.show()
			const response = await productInfo(request)
			const ok = unpackResponse(response)
			return ok.toObject()
		} catch (error) {
			handleError(error)
		} finally {
			Loading.hide()
		}
	},

	async addProduct({ getters, dispatch, commit }, { id, count }): Promise<void> {
		const product =
			getters.productById(id) ||
			await dispatch('fetchProductInfo', { id })
		if (!product) return
		commit('addProduct', { product, count }) // => src/store/mutations.ts: addProduct
	},

	async setProduct({ getters, dispatch, commit }, { id, count }): Promise<void> {
		const product =
			getters.productById(id) ||
			await dispatch('fetchProductInfo', { id })
		if (!product) return
		commit('setProduct', { product, count }) // => src/store/mutations.ts: setProduct
	},

	deleteProduct({ commit }, payload) {
		commit('deleteProduct', payload)
	},

	setState: ({ commit }, status) => {
		commit('setState', status)
	},

	async placeOrder({ commit, getters, state: { userInfo, products, discount } }) {
		if (products.length === 0) {
			handleError(Error(i18n.gettext('Your shopping cart is empty')))
			return
		}

		const purchaseMap: { [id: string]: number } = {}

		const survey = new CustomerSurvey()
		if (config.sfEnabled(SurveyField.FIO)) survey.setFio(userInfo.fullName)
		if (config.sfEnabled(SurveyField.MAIL)) survey.setEmail(userInfo.email)
		if (config.sfEnabled(SurveyField.PHONE)) survey.setPhone(userInfo.phone)
		if (config.sfEnabled(SurveyField.POSTCODE)) survey.setPostcode(userInfo.postcode)
		if (config.sfEnabled(SurveyField.CITY)) survey.setCity(userInfo.city)
		if (config.sfEnabled(SurveyField.ADDRESS)) survey.setAddress(userInfo.address)
		if (config.sfEnabled(SurveyField.ACM)) survey.setAcceptMarketingMessages(userInfo.acceptMarketingMessages)
		if (config.sfEnabled(SurveyField.DNTSNDTO3RDPRT)) survey.setDntSendTo3rdParties(userInfo.dntSendTo3rdParties)

		const request = new PlaceOrderRequest()
		request.setSaleRegion(config.saleRegion)
		request.setSurvey(survey)
		request.setPaymentGateway(userInfo.paymentOption)
		if (config.deliveryEnabled) {
			request.setDeliveryId(userInfo.deliveryOption)
			request.setDeliveryPrice100(userInfo.deliveryPrice100)
		}
		if (config.commentFieldEnabled) {
			request.setComment(userInfo.orderComment)
		}
		request.setPurchaseList(
			products.map(({ id, count }) => {
				const position = new PurchasePosition()
				position.setProductId(id)
				position.setCount(count)
				purchaseMap[id] = count
				return position
			})
		)

		if (getters.isDiscount) {
			request.setPromoCode(discount.code)
		}

		const authSid = Cookie.get(AUTH_COOKIE_NAME)
		if (authSid) request.setAuthSid(authSid)

		if (config.dsUserToken) {
			request.setDsUserToken(config.dsUserToken)
		}

		request.setDomain(window.location.host)

		const placeOrder = promisify(client.placeOrder, client)

		try {
			const response = await placeOrder(request)
			const ok = unpackResponse(response)
			for (const id in purchaseMap) {
				const count = purchaseMap[id]
				commit('decreaseProductCount', { id, count })
			}
			// уходим на платежную систему, после оплаты ожидаем попасть на страницу приземления
			switch (ok.getResultCase()) {
				case PlaceOrderOk.ResultCase.REDIRECT_URL:
					const redirectUrl = ok.getRedirectUrl()
					window.location.assign(redirectUrl)
					break
				case PlaceOrderOk.ResultCase.HTML_FORM:
					window.document.getElementById('submitformplaceholder')!.innerHTML = ok.getHtmlForm()
					const form = window.document.getElementById('submitform')! as HTMLFormElement
					console.log('form to submit: ', form) // eslint-disable-line
					form.submit()
					break
				default:
					console.log('unknown result case: ', ok.getResultCase(), ', response: ', ok) // eslint-disable-line
			}
		} catch (error) {
			handleError(error)
			throw error // пробрасываем ошибку дальше
		}
	},

	async validateEmail(ctx, email: string): Promise<boolean> {
		const request = new ValidateEmailRequest()
		request.setEmail(email)

		const validate = promisify(client.validateEmail, client)
		try {
			const response = await validate(request)
			if (!response) return false
			return response.getValid()
		} catch (error) {
			handleError(error)
			throw error
		}
	},

	async fetchDiscount(ctx, { code }): Promise<PbDiscount> {
		const request = new ValidateDiscountRequest()
		request.setSaleRegion(config.saleRegion)
		request.setCode(code)

		const fetch = promisify(client.validateDiscount, client)
		try {
			Loading.show()
			const response = await fetch(request)
			const ok = unpackResponse(response)
			return ok
		} catch (error) {
			throw error
		} finally {
			Loading.hide()
		}
	},

	async validatePromoCode({ dispatch }, code: string): Promise<boolean> {
		try {
			const discount: PbDiscount = await dispatch('fetchDiscount', { code })
			doValidateDiscount(discount)
			return true
		} catch (error) {
			if (error instanceof BasketError && error.getCode() === BasketError.Code.NOT_FOUND) {
				handleError(DiscountError.notFound())
				return false
			} else {
				handleError(error)
				throw error
			}
		}
	},

	async setDiscount({ dispatch, commit }, code: string) {
		try {
			const discount: PbDiscount = (await dispatch('fetchDiscount', { code }))
			commit('setDiscount', discount)
		} catch (error) {
			commit('deleteDiscount')
		}
	},

	deleteDiscount({ commit }) {
		commit('deleteDiscount')
	},
}

function doValidateDiscount(discount: PbDiscount) {
	switch (discount.getTarget()) {
		case PbDiscount.Target.NONE:
			throw Error('unexpected target: ' + discount.getTarget())
		case PbDiscount.Target.PRODUCTS_LIST:
			if (discount.getProductIdsList().length === 0) throw Error('Invalid discount, products missing: ' + discount)
			// falls through
		case PbDiscount.Target.WHOLE_BASKET:
		case PbDiscount.Target.PRODUCTS_ONLY:
		case PbDiscount.Target.DELIVERY_ONLY:
			switch (discount.getValueCase()) {
				case PbDiscount.ValueCase.PERCENT:
					if (discount.getPercent() <= 0) throw Error('Invalid discount, percent missing: ' + discount)
					break
				case PbDiscount.ValueCase.AMOUNT:
					if (discount.getAmount() <= 0) throw Error('Invalid discount, amount missing: ' + discount)
					break
				default:
					throw Error('unexpected discount value: ' + discount.getValueCase())
			}
			break
		default:
			throw Error('unexpected target: ' + discount.getTarget())
	}
}
